import { Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "./pages/auth/LoginPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { BackofficeLoginPage } from "./pages/backoffice/BackofficeLoginPage";
import { AcceptStaffInvitePage } from "./pages/backoffice/AcceptStaffInvitePage";
import { BackofficeOverviewPage } from "./pages/backoffice/OverviewPage";
import { TenantsListPage } from "./pages/backoffice/tenants/TenantsListPage";
import { TenantFormPage } from "./pages/backoffice/tenants/TenantFormPage";
import { TenantDetailPage } from "./pages/backoffice/tenants/TenantDetailPage";
import { BackofficeUsersListPage } from "./pages/backoffice/users/UsersListPage";
import { BackofficeUserDetailPage } from "./pages/backoffice/users/UserDetailPage";
import { ResetRequestsPage } from "./pages/backoffice/reset-requests/ResetRequestsPage";
import { StaffPage } from "./pages/backoffice/staff/StaffPage";
import { BackofficeProtectedRoute } from "./components/BackofficeProtectedRoute";
import { BackofficeShell } from "./components/layout/BackofficeShell";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { CompanyListPage } from "./pages/companies/CompanyListPage";
import { CompanyFormPage } from "./pages/companies/CompanyFormPage";
import { CompanyDetailPage } from "./pages/companies/CompanyDetailPage";
import { DriverListPage } from "./pages/drivers/DriverListPage";
import { DriverFormPage } from "./pages/drivers/DriverFormPage";
import { DriverDetailPage } from "./pages/drivers/DriverDetailPage";
import { VehicleListPage } from "./pages/vehicles/VehicleListPage";
import { VehicleFormPage } from "./pages/vehicles/VehicleFormPage";
import { VehicleDetailPage } from "./pages/vehicles/VehicleDetailPage";
import { FreightListPage } from "./pages/freights/FreightListPage";
import { FreightFormPage } from "./pages/freights/FreightFormPage";
import { FreightDetailPage } from "./pages/freights/FreightDetailPage";
import { InvoiceListPage } from "./pages/invoices/InvoiceListPage";
import { InvoiceFormPage } from "./pages/invoices/InvoiceFormPage";
import { InvoiceDetailPage } from "./pages/invoices/InvoiceDetailPage";
import { RepaymentListPage } from "./pages/repayments/RepaymentListPage";
import { RepaymentConsolidatedPage } from "./pages/repayments/RepaymentConsolidatedPage";
import { CashAccountListPage } from "./pages/cash/accounts/CashAccountListPage";
import { CashAccountFormPage } from "./pages/cash/accounts/CashAccountFormPage";
import { CashCategoryListPage } from "./pages/cash/categories/CashCategoryListPage";
import { CashFlowPage } from "./pages/cash/CashFlowPage";
import { EmployeeListPage } from "./pages/employees/EmployeeListPage";
import { EmployeeFormPage } from "./pages/employees/EmployeeFormPage";
import { EmployeeDetailPage } from "./pages/employees/EmployeeDetailPage";
import { OperationalExpenseListPage } from "./pages/expenses/OperationalExpenseListPage";
import { OperationalExpenseDetailPage } from "./pages/expenses/OperationalExpenseDetailPage";
import { ReportsPage } from "./pages/reports/ReportsPage";
import { FinancialObligationListPage } from "./pages/obligations/FinancialObligationListPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppShell } from "./components/layout/AppShell";
import { ErrorPage } from "./pages/errors/ErrorPage";
import { isAuthenticated } from "./lib/auth";

function ProtectedShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function BackofficeProtected({ children }: { readonly children: React.ReactNode }) {
  return (
    <BackofficeProtectedRoute>
      <BackofficeShell>{children}</BackofficeShell>
    </BackofficeProtectedRoute>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      {/* Backoffice — autenticação separada */}
      <Route path="/backoffice/login" element={<BackofficeLoginPage />} />
      <Route path="/backoffice/accept-invite/:token" element={<AcceptStaffInvitePage />} />
      <Route
        path="/backoffice"
        element={
          <BackofficeProtected>
            <BackofficeOverviewPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/tenants"
        element={
          <BackofficeProtected>
            <TenantsListPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/tenants/new"
        element={
          <BackofficeProtected>
            <TenantFormPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/tenants/:id"
        element={
          <BackofficeProtected>
            <TenantDetailPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/tenants/:id/edit"
        element={
          <BackofficeProtected>
            <TenantFormPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/users"
        element={
          <BackofficeProtected>
            <BackofficeUsersListPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/users/:id"
        element={
          <BackofficeProtected>
            <BackofficeUserDetailPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/reset-requests"
        element={
          <BackofficeProtected>
            <ResetRequestsPage />
          </BackofficeProtected>
        }
      />
      <Route
        path="/backoffice/staff"
        element={
          <BackofficeProtected>
            <StaffPage />
          </BackofficeProtected>
        }
      />
      <Route path="/backoffice/403" element={<ErrorPage code={403} />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedShell>
            <DashboardPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedShell>
            <SettingsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/cash"
        element={
          <ProtectedShell>
            <CashFlowPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/cash/accounts"
        element={
          <ProtectedShell>
            <CashAccountListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/cash/accounts/new"
        element={
          <ProtectedShell>
            <CashAccountFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/cash/accounts/:id/edit"
        element={
          <ProtectedShell>
            <CashAccountFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/cash/categories"
        element={
          <ProtectedShell>
            <CashCategoryListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedShell>
            <EmployeeListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/employees/new"
        element={
          <ProtectedShell>
            <EmployeeFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <ProtectedShell>
            <EmployeeDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/employees/:id/edit"
        element={
          <ProtectedShell>
            <EmployeeFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/operational-expenses"
        element={
          <ProtectedShell>
            <OperationalExpenseListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/operational-expenses/:id"
        element={
          <ProtectedShell>
            <OperationalExpenseDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedShell>
            <ReportsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/financial-obligations"
        element={
          <ProtectedShell>
            <FinancialObligationListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedShell>
            <CompanyListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/companies/new"
        element={
          <ProtectedShell>
            <CompanyFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedShell>
            <CompanyDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/companies/:id/edit"
        element={
          <ProtectedShell>
            <CompanyFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/drivers"
        element={
          <ProtectedShell>
            <DriverListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/drivers/new"
        element={
          <ProtectedShell>
            <DriverFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/drivers/:id"
        element={
          <ProtectedShell>
            <DriverDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/drivers/:id/edit"
        element={
          <ProtectedShell>
            <DriverFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/vehicles"
        element={
          <ProtectedShell>
            <VehicleListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/vehicles/new"
        element={
          <ProtectedShell>
            <VehicleFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/vehicles/:id"
        element={
          <ProtectedShell>
            <VehicleDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/vehicles/:id/edit"
        element={
          <ProtectedShell>
            <VehicleFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/freights"
        element={
          <ProtectedShell>
            <FreightListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/freights/new"
        element={
          <ProtectedShell>
            <FreightFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/freights/:id"
        element={
          <ProtectedShell>
            <FreightDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/freights/:id/edit"
        element={
          <ProtectedShell>
            <FreightFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedShell>
            <InvoiceListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <ProtectedShell>
            <InvoiceFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedShell>
            <InvoiceDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/invoices/:id/edit"
        element={
          <ProtectedShell>
            <InvoiceFormPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/repayments"
        element={
          <ProtectedShell>
            <RepaymentListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/repayments/consolidated"
        element={
          <ProtectedShell>
            <RepaymentConsolidatedPage />
          </ProtectedShell>
        }
      />
      <Route path="/403" element={<ErrorPage code={403} />} />
      <Route path="/500" element={<ErrorPage code={500} />} />
      <Route
        path="*"
        element={
          isAuthenticated()
            ? <ErrorPage code={404} />
            : <Navigate to="/login" replace />
        }
      />
    </Routes>
  );
}

export default App;
